// Чтение claims из access-токена Identix (тенант data1861):
// `sub` — числовой id пользователя (= users.identix_id; локалпарт Matrix-id @sub:server),
// `preferred_username` (email) — идентификатор для Matrix-login (com.graintrade.login.jwt),
// `roles` — ПЛОСКИЙ claim-массив ролей (USER/AGENT/...); Identix НЕ использует realm_access,
// `password_change_required` — вход по временному паролю (требуется форс-смена пароля).
// Подпись НЕ проверяем (клиент только читает claims; доверие к токену обеспечивает транспорт/бэкенд).

export interface AccessTokenClaims {
  /** Identix sub — числовой id пользователя (локалпарт Matrix-id @sub:server). */
  readonly sub: string;
  /** email — identifier для Matrix-login. */
  readonly preferredUsername: string;
  /** Роли (USER, AGENT, org_owner, …) — из плоского claim `roles`. */
  readonly roles: readonly string[];
  /** true — вход по временному паролю: до продолжения работы нужно сменить пароль. */
  readonly passwordChangeRequired: boolean;
  /**
   * Актор имперсонации (реальный админ) — из claim `act`. Присутствует ТОЛЬКО в токене первого обмена;
   * после refresh Identix его НЕ переиздаёт, поэтому факт имперсонации фиксируется в tokenStore, а не
   * вычисляется из каждого токена (см. импер-маркер).
   */
  readonly impersonator?: { readonly sub: string; readonly username: string };
  /** unix-секунды истечения, если есть. */
  readonly exp?: number;
}

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Распарсить claims access-токена; null — токен пуст/битый/без обязательных полей. */
export function decodeAccessToken(token: string | undefined | null): AccessTokenClaims | null {
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) {
    return null;
  }
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1])) as Record<string, unknown>;
    const sub = typeof payload.sub === 'string' ? payload.sub : '';
    const preferredUsername =
      typeof payload.preferred_username === 'string' ? payload.preferred_username : '';
    if (!sub || !preferredUsername) {
      return null;
    }
    const roles = Array.isArray(payload.roles)
      ? payload.roles.filter((role): role is string => typeof role === 'string')
      : [];
    return {
      sub,
      preferredUsername,
      roles,
      passwordChangeRequired: payload.password_change_required === true,
      impersonator: parseActor(payload.act),
      exp: typeof payload.exp === 'number' ? payload.exp : undefined,
    };
  } catch {
    return null;
  }
}

export function hasRole(claims: AccessTokenClaims | null, role: string): boolean {
  return claims?.roles.includes(role) ?? false;
}

/**
 * Разобрать claim `act` имперсонации ({ sub, preferred_username }); undefined — обычный (не импер) токен.
 * ПРИСУТСТВИЕ `act` (любой объект) = имперсонация — маркер должен ставиться даже при нестандартных полях
 * актора (напр. числовой sub), иначе признак имперсонации молча теряется. Поля мягко коэрсим.
 */
function parseActor(act: unknown): { sub: string; username: string } | undefined {
  if (!act || typeof act !== 'object') {
    return undefined;
  }
  const actor = act as Record<string, unknown>;
  const sub =
    typeof actor.sub === 'string' ? actor.sub : typeof actor.sub === 'number' ? String(actor.sub) : '';
  const username = typeof actor.preferred_username === 'string' ? actor.preferred_username : '';
  return { sub, username };
}
